import { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { KeenIcon } from '@/components/keenicons';
import { LazyFormikSelect, PriceInput } from '@/components/form';
import { useUserOptions } from '@/hooks';
import letterTemplateService from '@/services/letters/letterTemplateService';
import demandLetterService from '@/services/letters/demandLetterService';

const schema = Yup.object().shape({
  letter_template_id: Yup.string().required('Pick a template'),
  days_to_respond: Yup.number().min(1, 'At least 1 day').max(365, 'At most 365 days').required('Required'),
  title: Yup.string().max(255).nullable(),
  demand_amount: Yup.string().nullable(),
});

/**
 * "New demand letter" — pick the template and the terms; the recipient block
 * is prefilled from the matter's adjuster / carrier fields by the server and
 * edited on the letter page.
 */
const GenerateDemandModal = ({ open, onClose, matterId, onCreated }) => {
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });
  const [templates, setTemplates] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      letterTemplateService.list({ matter_id: matterId }),
      demandLetterService.rules(),
    ])
      .then(([t, r]) => {
        if (cancelled) return;
        setTemplates(t?.data || []);
        setStates(r?.data?.states || []);
      })
      .catch((error) => {
        console.error('Error', error);
        toast.error('Could not load templates.', { description: 'Please try again in a moment.' });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, matterId]);

  if (!open) return null;

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const { status, data } = await demandLetterService.generate({
        matter_id: matterId,
        letter_template_id: values.letter_template_id,
        attorney_id: values.attorney_id || null,
        title: values.title || null,
        demand_amount: values.demand_amount || null,
        days_to_respond: values.days_to_respond,
        jurisdiction: values.jurisdiction || null,
      });
      if (status) {
        toast.success('Demand letter created.', { description: 'Fill in the highlighted prompts, then send.' });
        onCreated?.(data.letter);
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Could not create the demand letter.', { description: error?.message || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-wrapper modal-animate-fade">
      <Formik
        initialValues={{ letter_template_id: '', attorney_id: '', title: '', demand_amount: '', days_to_respond: 33, jurisdiction: '' }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form className="modal-animate-scale modal-body w-4/5 max-w-lg relative">
            <h3 className="card-title mb-5">New Demand Letter</h3>
            <button type="button" onClick={onClose} className="modal-close-btn"><KeenIcon icon="cross" /></button>

            <div className="mb-4">
              <label className="form-label mb-1">Template <span className="text-red-600">*</span></label>
              <Field
                as="select"
                name="letter_template_id"
                className="select"
                disabled={loading}
                onChange={(e) => {
                  const id = e.target.value;
                  setFieldValue('letter_template_id', id);
                  const t = templates.find((x) => x.id === id);
                  if (t) {
                    setFieldValue('days_to_respond', t.default_days_to_respond || 30);
                    setFieldValue('jurisdiction', t.jurisdiction || '');
                  }
                }}
              >
                <option value="">{loading ? 'Loading…' : 'Choose a template'}</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.record_type ? ` · ${t.record_type.name}` : ' · Any matter'}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="letter_template_id" component="div" className="text-red-500 text-xs mt-1" />
              {values.letter_template_id && (
                <p className="text-xs text-gray-500 mt-1">{templates.find((x) => x.id === values.letter_template_id)?.description}</p>
              )}
              {!loading && templates.length === 0 && (
                <p className="text-xs text-warning mt-1">No active template fits this matter&apos;s practice area. Add one under Documents → Letter Templates.</p>
              )}
            </div>

            <div className="mb-4">
              <label className="form-label mb-1">Signing attorney</label>
              <LazyFormikSelect name="attorney_id" placeholder="Default: first assigned user" loadOptions={loadUserOptions} pageSize={20} />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="form-label mb-1">Demand amount</label>
                <PriceInput name="demand_amount" prefix="$" placeholder="Policy limits" />
              </div>
              <div>
                <label className="form-label mb-1">Days to respond <span className="text-red-600">*</span></label>
                <Field name="days_to_respond" type="number" min={1} max={365} className="input" />
                <ErrorMessage name="days_to_respond" component="div" className="text-red-500 text-xs mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="form-label mb-1">Jurisdiction</label>
                <Field as="select" name="jurisdiction" className="select">
                  <option value="">{"From the matter's State of Loss"}</option>
                  {states.map((s) => <option key={s.code} value={s.code}>{s.name}{s.statute ? ` (${s.statute})` : ''}</option>)}
                </Field>
              </div>
              <div>
                <label className="form-label mb-1">Title</label>
                <Field name="title" className="input" placeholder="Defaults to template · carrier" />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" className="btn btn-sm btn-light" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-sm btn-primary" disabled={submitting || loading}>
                {submitting ? 'Creating…' : 'Create draft'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export { GenerateDemandModal };
